import { StyledPropsWithChildren } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import React, { useContext } from 'react';

type NativeTextProps = JSX.IntrinsicElements['span'] &
  JSX.IntrinsicElements['label'] &
  JSX.IntrinsicElements['p'];
type SelectedTextProps = Pick<NativeTextProps, 'id' | 'role' | 'onClick' | 'htmlFor' | 'role'>;

interface Props {
  /**
   * Type of the element
   */
  type?: 'span' | 'label' | 'p';
  /**
   * aria label
   */
  ariaLabel?: NativeTextProps['aria-label'];
  /**
   * aria level
   */
  ariaLevel?: NativeTextProps['aria-level'];
  /**
   * hide content to screen readers
   */
  ariaHidden?: NativeTextProps['aria-hidden'];
  /**
   * Allows the creation of a data-react-cmp-owner data-attr
   */
  dataReactCmpOwner?: string;
  /**
   * aria live
   */
  ariaLive?: NativeTextProps['aria-live'];
  /**
   * aria Atomic
   */
  ariaAtomic?: NativeTextProps['aria-atomic'];
  /**
   * Id for Test
   */
  dataTestId?: string;
  /**
   * innerHTML
   */
  dangerouslySetInnerHTML?: {
    __html: string;
  };
  focusReferences?: NativeTextProps['ref'];
}

export type TextProps = StyledPropsWithChildren<SelectedTextProps & Props>;

function Text({
  children,
  id,
  type = 'span',
  htmlFor,
  ariaLabel,
  ariaHidden,
  role,
  dataReactCmpOwner,
  dataTestId,
  onClick,
  ariaLevel,
  ariaLive,
  ariaAtomic,
  dangerouslySetInnerHTML,
  focusReferences,
  ...props
}: TextProps) {
  const Tag = type;
  const dataAttrs = dataReactCmpOwner ? { 'data-react-cmp-owner': dataReactCmpOwner } : {};
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  return (
    <Tag
      className={`fr-${type} ${className}`}
      id={id}
      style={style}
      htmlFor={htmlFor}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      aria-level={ariaLevel}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
      role={role}
      onClick={onClick}
      data-testid={dataTestId}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      ref={focusReferences}
      {...dataAttrs}
    >
      {children}
    </Tag>
  );
}

Text.componentName = 'Text';

export default Text;
